import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Minus,
  Plus,
  Save,
  Settings,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

interface TemplateTimeInterval {
  startTime: string;
  endTime: string;
}

interface TemplateBreakRule {
  afterHours: number;
  breakType: string;
  duration: number;
  isPaid: boolean;
  isRequired: boolean;
}

interface TemplateFormData {
  name: string;
  description: string;
  position: string;
  scheduleType: string;
  days: number[];
  color: string;
  icon: string;
  timeIntervals: TemplateTimeInterval[];
  breakRules: TemplateBreakRule[];
}

interface ShiftTemplate extends TemplateFormData {
  id: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface TemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  template?: ShiftTemplate | null;
  onSave: (data: TemplateFormData) => Promise<void>;
}

const DAYS_OF_WEEK = [
  { value: 1, label: "Monday", short: "Mon" },
  { value: 2, label: "Tuesday", short: "Tue" },
  { value: 3, label: "Wednesday", short: "Wed" },
  { value: 4, label: "Thursday", short: "Thu" },
  { value: 5, label: "Friday", short: "Fri" },
  { value: 6, label: "Saturday", short: "Sat" },
  { value: 7, label: "Sunday", short: "Sun" },
];

const SCHEDULE_TYPES = [
  { value: "weekly", label: "Weekly", description: "Every week" },
  {
    value: "biweekly_even",
    label: "Bi-weekly (Even Weeks)",
    description: "Every even week (2, 4, 6...)",
  },
  {
    value: "biweekly_odd",
    label: "Bi-weekly (Odd Weeks)",
    description: "Every odd week (1, 3, 5...)",
  },
];

const BREAK_TYPES = [
  { value: "REST", label: "15-min Rest Break" },
  { value: "BREAK", label: "Standard Break" },
  { value: "MEAL", label: "Meal Break" },
  { value: "TRAINING", label: "Training Break" },
  { value: "PERSONAL", label: "Personal Time" },
];

const PREDEFINED_COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#84cc16",
  "#f97316",
  "#ec4899",
  "#6366f1",
  "#14b8a6",
  "#f43f5e",
];

const initialFormData: TemplateFormData = {
  name: "",
  description: "",
  position: "",
  scheduleType: "weekly",
  days: [1, 2, 3, 4, 5], // Monday to Friday
  color: "#3b82f6",
  icon: "",
  timeIntervals: [{ startTime: "09:00", endTime: "17:00" }],
  breakRules: [],
};

export function TemplateDialog({ isOpen, onClose, template, onSave }: TemplateDialogProps) {
  const [formData, setFormData] = useState<TemplateFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);

  const isEditing = !!template;

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      if (template) {
        setFormData({
          name: template.name,
          description: template.description || "",
          position: template.position || "",
          scheduleType: template.scheduleType,
          days: [...template.days],
          color: template.color || "#3b82f6",
          icon: template.icon || "",
          timeIntervals:
            template.timeIntervals.length > 0
              ? template.timeIntervals.map((interval) => ({
                  startTime: interval.startTime,
                  endTime: interval.endTime,
                }))
              : [{ startTime: "09:00", endTime: "17:00" }],
          breakRules: template.breakRules.map((rule) => ({
            afterHours: rule.afterHours,
            breakType: rule.breakType,
            duration: rule.duration,
            isPaid: rule.isPaid,
            isRequired: rule.isRequired,
          })),
        });
      } else {
        setFormData(initialFormData);
      }
      setErrors({});
      setShowAdvanced(false); // Reset advanced section
    }
  }, [isOpen, template]);

  // Handle schedule type change to set appropriate defaults
  const handleScheduleTypeChange = (newScheduleType: string) => {
    setFormData((prev) => {
      let newDays = prev.days;

      // Set sensible defaults based on schedule type
      if (newScheduleType === "weekly" && prev.days.length === 0) {
        newDays = [1, 2, 3, 4, 5]; // Mon-Fri default for weekly
      } else if (
        (newScheduleType === "biweekly_even" || newScheduleType === "biweekly_odd") &&
        prev.days.length === 0
      ) {
        newDays = [1, 3, 5]; // Mon, Wed, Fri default for bi-weekly
      }

      return {
        ...prev,
        scheduleType: newScheduleType,
        days: newDays,
      };
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Template name is required";
    }

    if (formData.days.length === 0) {
      if (formData.scheduleType === "weekly") {
        newErrors.days = "Select at least one day of the week";
      } else if (
        formData.scheduleType === "biweekly_even" ||
        formData.scheduleType === "biweekly_odd"
      ) {
        newErrors.days = "Select at least one day for the bi-weekly schedule";
      }
    }

    if (formData.timeIntervals.length === 0) {
      newErrors.timeIntervals = "At least one time interval is required";
    }

    // Validate time intervals
    formData.timeIntervals.forEach((interval, index) => {
      if (!interval.startTime || !interval.endTime) {
        newErrors[`timeInterval_${index}`] = "Both start and end times are required";
      }
      if (interval.startTime >= interval.endTime) {
        newErrors[`timeInterval_${index}`] = "End time must be after start time";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({}); // Clear previous errors

    try {
      await onSave(formData);
    } catch (error) {
      console.error("Failed to save template:", error);
      setErrors({
        submit:
          error instanceof Error ? error.message : "Failed to save template. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addTimeInterval = () => {
    setFormData((prev) => ({
      ...prev,
      timeIntervals: [...prev.timeIntervals, { startTime: "09:00", endTime: "17:00" }],
    }));
  };

  const removeTimeInterval = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      timeIntervals: prev.timeIntervals.filter((_, i) => i !== index),
    }));
  };

  const updateTimeInterval = (index: number, field: keyof TemplateTimeInterval, value: string) => {
    setFormData((prev) => ({
      ...prev,
      timeIntervals: prev.timeIntervals.map((interval, i) =>
        i === index ? { ...interval, [field]: value } : interval
      ),
    }));
  };

  const addBreakRule = () => {
    const newBreak: TemplateBreakRule = {
      afterHours: 4,
      breakType: "BREAK",
      duration: 15,
      isPaid: false,
      isRequired: true,
    };
    setFormData((prev) => ({
      ...prev,
      breakRules: [...prev.breakRules, newBreak],
    }));
  };

  const removeBreakRule = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      breakRules: prev.breakRules.filter((_, i) => i !== index),
    }));
  };

  const updateBreakRule = (index: number, field: keyof TemplateBreakRule, value: any) => {
    setFormData((prev) => ({
      ...prev,
      breakRules: prev.breakRules.map((rule, i) =>
        i === index ? { ...rule, [field]: value } : rule
      ),
    }));
  };

  const toggleDay = (day: number) => {
    setFormData((prev) => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day].sort(),
    }));
  };

  const calculateTotalHours = (): number => {
    return formData.timeIntervals.reduce((total, interval) => {
      const start = new Date(`2000-01-01T${interval.startTime}:00`);
      const end = new Date(`2000-01-01T${interval.endTime}:00`);
      const diff = end.getTime() - start.getTime();
      return total + diff / (1000 * 60 * 60);
    }, 0);
  };

  const calculateTotalBreakTime = (): number => {
    return formData.breakRules.reduce((total, rule) => total + rule.duration, 0);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {isEditing ? "Edit Shift Template" : "Create Shift Template"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update your shift template. Set the basic schedule first, then configure advanced options if needed."
              : "Create a reusable shift template for your team. Start with the basic schedule - you can add advanced features later."}
          </DialogDescription>
        </DialogHeader>

        {errors.submit && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errors.submit}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Section */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Basic Information</CardTitle>
              <CardDescription>Set up the basic details for your shift template</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Template Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Full Time Stylist, Weekend Reception"
                    className={errors.name ? "border-destructive" : ""}
                  />
                  {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="position">Position</Label>
                  <Input
                    id="position"
                    value={formData.position}
                    onChange={(e) => setFormData((prev) => ({ ...prev, position: e.target.value }))}
                    placeholder="e.g., Stylist, Receptionist"
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional - helps organize templates
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="Brief description of this shift template..."
                  rows={2}
                />
                <p className="text-xs text-muted-foreground">
                  Optional - helps identify template purpose
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Schedule Section */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Schedule</CardTitle>
              <CardDescription>
                Set when this template applies and the working hours
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Schedule Type *</Label>
                  <Select value={formData.scheduleType} onValueChange={handleScheduleTypeChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHEDULE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex flex-col">
                            <span>{type.label}</span>
                            <span className="text-xs text-muted-foreground">
                              {type.description}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.scheduleType === "weekly" && (
                    <p className="text-xs text-muted-foreground">
                      This template repeats every week
                    </p>
                  )}
                  {formData.scheduleType === "biweekly_even" && (
                    <p className="text-xs text-muted-foreground">
                      This template runs on even-numbered weeks (2, 4, 6, 8...)
                    </p>
                  )}
                  {formData.scheduleType === "biweekly_odd" && (
                    <p className="text-xs text-muted-foreground">
                      This template runs on odd-numbered weeks (1, 3, 5, 7...)
                    </p>
                  )}
                </div>

                {/* Days Selection */}
                <div className="space-y-2">
                  <Label>Working Days *</Label>
                  <div className="flex gap-1 flex-wrap">
                    {DAYS_OF_WEEK.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleDay(day.value)}
                        className={`px-3 py-2 text-sm rounded-md transition-colors font-medium ${
                          formData.days.includes(day.value)
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground hover:bg-muted/80"
                        }`}
                      >
                        {day.short}
                      </button>
                    ))}
                  </div>
                  {errors.days && <p className="text-xs text-destructive">{errors.days}</p>}
                  <p className="text-xs text-muted-foreground">
                    {formData.scheduleType === "weekly" &&
                      "Select the days of the week for this template"}
                    {(formData.scheduleType === "biweekly_even" ||
                      formData.scheduleType === "biweekly_odd") &&
                      "Select which days to work during the active weeks"}
                  </p>
                </div>
              </div>

              {/* Working Hours */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="text-base font-medium">Working Hours *</Label>
                    <p className="text-sm text-muted-foreground">
                      Set the daily working hours for this template
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addTimeInterval}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Split Shift
                  </Button>
                </div>
                {errors.timeIntervals && (
                  <Alert variant="destructive" className="mb-3">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{errors.timeIntervals}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-3">
                  {formData.timeIntervals.map((interval, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30"
                    >
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm">Start Time</Label>
                          <Input
                            type="time"
                            value={interval.startTime}
                            onChange={(e) => updateTimeInterval(index, "startTime", e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">End Time</Label>
                          <Input
                            type="time"
                            value={interval.endTime}
                            onChange={(e) => updateTimeInterval(index, "endTime", e.target.value)}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      {formData.timeIntervals.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTimeInterval(index)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Advanced Options */}
          <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
            <CollapsibleTrigger asChild>
              <Button type="button" variant="ghost" className="w-full justify-between p-4 h-auto">
                <div className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  <span className="text-base font-medium">Advanced Options</span>
                </div>
                {showAdvanced ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 mt-4">
              {/* Visual Settings */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-base">Visual Settings</CardTitle>
                  <CardDescription>Customize how this template appears</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label>Template Color</Label>
                    <div className="flex gap-2 flex-wrap items-center">
                      {PREDEFINED_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                            formData.color === color
                              ? "border-foreground scale-110"
                              : "border-border"
                          }`}
                          style={{ backgroundColor: color }}
                          onClick={() => setFormData((prev) => ({ ...prev, color }))}
                        />
                      ))}
                      <Input
                        type="color"
                        value={formData.color}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, color: e.target.value }))
                        }
                        className="w-8 h-8 p-0 border-2 rounded-full"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Choose a color to help identify this template
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Break Rules */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-base flex items-center justify-between">
                    Automatic Breaks
                    <Button type="button" variant="outline" size="sm" onClick={addBreakRule}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Rule
                    </Button>
                  </CardTitle>
                  <CardDescription>
                    Set up rules for automatic break scheduling based on shift length
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {formData.breakRules.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <p className="text-sm">No automatic break rules configured</p>
                      <p className="text-xs mt-1">
                        Breaks will be added manually to individual shifts
                      </p>
                    </div>
                  ) : (
                    formData.breakRules.map((rule, index) => (
                      <div key={index} className="p-4 border rounded-lg bg-muted/30">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <div>
                            <Label className="text-sm">Break Type</Label>
                            <Select
                              value={rule.breakType}
                              onValueChange={(value) => updateBreakRule(index, "breakType", value)}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {BREAK_TYPES.map((type) => (
                                  <SelectItem key={type.value} value={type.value}>
                                    {type.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-sm">After Hours</Label>
                            <Input
                              type="number"
                              value={rule.afterHours}
                              onChange={(e) =>
                                updateBreakRule(
                                  index,
                                  "afterHours",
                                  Number.parseFloat(e.target.value) || 0
                                )
                              }
                              min="0.5"
                              max="12"
                              step="0.5"
                              className="mt-1"
                              placeholder="4"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Hours worked before break
                            </p>
                          </div>
                          <div>
                            <Label className="text-sm">Duration</Label>
                            <Input
                              type="number"
                              value={rule.duration}
                              onChange={(e) =>
                                updateBreakRule(
                                  index,
                                  "duration",
                                  Number.parseInt(e.target.value, 10) || 0
                                )
                              }
                              min="5"
                              max="180"
                              className="mt-1"
                              placeholder="30"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Break length in minutes
                            </p>
                          </div>
                          <div className="flex flex-col">
                            <Label className="text-sm mb-2">Options</Label>
                            <div className="flex items-center justify-between">
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={rule.isPaid}
                                  onChange={(e) =>
                                    updateBreakRule(index, "isPaid", e.target.checked)
                                  }
                                  className="rounded"
                                />
                                Paid break
                              </label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeBreakRule(index)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </CollapsibleContent>
          </Collapsible>

          {/* Quick Summary */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {formData.scheduleType === "weekly" ? "Hours per day:" : "Hours per active day:"}
              </span>
              <span className="font-medium">{calculateTotalHours().toFixed(1)} hours</span>
            </div>
            {formData.breakRules.length > 0 && (
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-muted-foreground">Break time:</span>
                <span className="font-medium">{calculateTotalBreakTime()} minutes</span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-muted-foreground">Schedule:</span>
              <span className="font-medium text-xs">
                {formData.scheduleType === "weekly" && `${formData.days.length} days per week`}
                {formData.scheduleType === "biweekly_even" &&
                  `${formData.days.length} days every even week`}
                {formData.scheduleType === "biweekly_odd" &&
                  `${formData.days.length} days every odd week`}
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? "Saving..." : isEditing ? "Update Template" : "Create Template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
